import { useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import {
  ASSIGNMENT_PRIORITIES,
  DEFAULT_ASSIGNMENT_PRIORITY,
  PAYABLE_STAGES,
  assignmentWarnings,
  isPayableStage,
  type AssignmentPriority,
  type PayableStage,
  type StaffWorkload,
  type WorkflowStage,
} from '@tailoring-crm/shared';
import { useWorkload } from '../../lib/assignments/use-assignments.js';
import {
  cn,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@tailoring-crm/ui';

/**
 * Who gets this job.
 *
 * The picker shows what is already on each person's table, because the decision it exists to
 * support is "who is free?" and a bare list of names cannot answer it. The counts come from the
 * server; the warnings beside a name come from the SAME shared function the API applies after
 * the write, so what the owner is told before they tap and what comes back afterwards cannot
 * drift apart.
 *
 * Nothing here blocks. A busy person and somebody outside their usual stage are both selectable,
 * flagged, and assigned if the owner says so — they know something the counts do not.
 */
export interface AssignDialogProps {
  /** What is being handed out, for the title. One garment, or "8 garments". */
  subject: string;
  /** The garment's current stage, used to pick a sensible default. */
  currentStage?: WorkflowStage;
  /** Only these stages are offered. Defaults to every payable stage. */
  stages?: readonly PayableStage[];
  stageLabel: (stage: PayableStage) => string;
  /**
   * Reassignment asks WHY, in the same dialog rather than a step before it.
   *
   * The reason is what the original row keeps and what the API requires, so it has to be
   * collected — but a second modal in front of this one would mean somebody who dismissed it
   * could not reassign at all. One dialog, one confirm button, disabled until both halves are
   * answered.
   */
  requireReason?: boolean;
  pending?: boolean;
  onCancel: () => void;
  onConfirm: (input: AssignDialogResult) => void;
}

/**
 * What the dialog hands back.
 *
 * The four planning fields are OPTIONAL and stay absent when nobody touched them. A dialog that
 * sent `estimatedMinutes: 0` and `otMinutes: 0` for every assignment would fill the collection
 * with estimates nobody made — and "estimated at nothing" is a different claim from "not
 * estimated", which is exactly the distinction the null on the row exists to keep.
 */
export interface AssignDialogResult {
  assignedToUserId: string;
  stage: PayableStage;
  reason: string;
  estimatedMinutes?: number;
  priority?: AssignmentPriority;
  splittable?: boolean;
  otMinutes?: number;
}

/** An empty box is "not said", not zero. Anything unparseable is treated the same way. */
function wholeMinutes(value: string): number {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

/** The stage to offer first: the one the garment is at, if it is paid work; else stitching. */
function defaultStage(
  currentStage: WorkflowStage | undefined,
  allowed: readonly PayableStage[],
): PayableStage {
  if (currentStage && isPayableStage(currentStage) && allowed.includes(currentStage)) {
    return currentStage;
  }
  return allowed[0] ?? 'stitching';
}

export function AssignDialog({
  subject,
  currentStage,
  stages = PAYABLE_STAGES,
  stageLabel,
  requireReason = false,
  pending = false,
  onCancel,
  onConfirm,
}: AssignDialogProps): ReactElement {
  const { t } = useTranslation();
  const workload = useWorkload();

  const [stage, setStage] = useState<PayableStage>(() => defaultStage(currentStage, stages));
  const [userId, setUserId] = useState<string>('');
  const [reason, setReason] = useState('');
  const reasonReady = !requireReason || reason.trim().length >= 3;

  const [hours, setHours] = useState('');
  const [minutes, setMinutes] = useState('');
  const [priority, setPriority] = useState<AssignmentPriority>(DEFAULT_ASSIGNMENT_PRIORITY);
  const [splittable, setSplittable] = useState(false);
  const [overtime, setOvertime] = useState('');

  // Hours and minutes in the boxes, whole minutes on the wire. The two halves are one number
  // the moment they leave this component, and nothing downstream sees a duration any other way.
  const estimatedMinutes = wholeMinutes(hours) * 60 + wholeMinutes(minutes);
  const otMinutes = wholeMinutes(overtime);

  /** Only what was actually said. An untouched dialog adds nothing to the request. */
  const plan = (): Omit<AssignDialogResult, 'assignedToUserId' | 'stage' | 'reason'> => ({
    ...(estimatedMinutes > 0 ? { estimatedMinutes } : {}),
    ...(priority === DEFAULT_ASSIGNMENT_PRIORITY ? {} : { priority }),
    ...(splittable ? { splittable: true } : {}),
    ...(otMinutes > 0 ? { otMinutes } : {}),
  });

  // Deactivated people are not offered at all: they cannot sign in, so they cannot do the work.
  const candidates = useMemo(
    () => (workload.data?.items ?? []).filter((one) => one.active),
    [workload.data],
  );

  const warningsFor = (person: StaffWorkload): ReturnType<typeof assignmentWarnings> =>
    assignmentWarnings(
      {
        name: person.name,
        activeCount: person.openCount,
        specialisations: person.specialisations,
        active: person.active,
      },
      stage,
    );

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('assignments.assignTitle', { subject })}</DialogTitle>
          <DialogDescription>{t('assignments.assignHint')}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="assign-stage">{t('assignments.stageLabel')}</Label>
          <select
            id="assign-stage"
            value={stage}
            onChange={(event) => setStage(event.target.value as PayableStage)}
            className="h-11 rounded-md border border-border bg-background px-3 text-sm"
          >
            {stages.map((one) => (
              <option key={one} value={one}>
                {stageLabel(one)}
              </option>
            ))}
          </select>
        </div>

        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-sm font-medium">{t('assignments.whoLabel')}</legend>

          {workload.isPending ? (
            <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
          ) : candidates.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('assignments.noStaff')}</p>
          ) : (
            candidates.map((person) => {
              const warnings = warningsFor(person);
              const chosen = userId === person.userId;
              return (
                <label
                  key={person.userId}
                  className={cn(
                    'flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors',
                    chosen ? 'border-primary bg-accent' : 'border-border hover:bg-accent',
                  )}
                >
                  <input
                    type="radio"
                    name="assignee"
                    value={person.userId}
                    checked={chosen}
                    onChange={() => setUserId(person.userId)}
                    className="mt-1 size-4 shrink-0 accent-primary"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block font-medium">{person.name}</span>
                    <span className="block text-sm text-muted-foreground">
                      {t('assignments.openJobs', { count: person.openCount })}
                      {person.overdueCount > 0
                        ? ` · ${t('assignments.overdueJobs', { count: person.overdueCount })}`
                        : ''}
                    </span>
                    {warnings.map((warning) => (
                      <span
                        key={warning.code}
                        className="mt-1 flex items-center gap-1 text-sm text-warning"
                      >
                        <AlertTriangle aria-hidden="true" className="size-3.5 shrink-0" />
                        {warning.message}
                      </span>
                    ))}
                  </span>
                </label>
              );
            })
          )}
        </fieldset>

        {/*
          How long, how urgent, whether it can be split, and what overtime it allows — all
          optional, all at the moment of handing the work over, because that is the only moment
          somebody has the garment, the person and the date in their head at the same time.
        */}
        <div className="flex flex-wrap items-end gap-3">
          <fieldset className="flex flex-col gap-1.5">
            <legend className="mb-1 text-sm font-medium">{t('assignments.estimateLabel')}</legend>
            <div className="flex items-center gap-2">
              <Input
                id="assign-estimate-hours"
                type="number"
                inputMode="numeric"
                min="0"
                className="w-20"
                aria-label={t('assignments.estimateHours')}
                placeholder={t('assignments.estimateHours')}
                value={hours}
                onChange={(event) => setHours(event.target.value)}
              />
              <span aria-hidden="true">:</span>
              <Input
                id="assign-estimate-minutes"
                type="number"
                inputMode="numeric"
                min="0"
                max="59"
                className="w-20"
                aria-label={t('assignments.estimateMinutes')}
                placeholder={t('assignments.estimateMinutes')}
                value={minutes}
                onChange={(event) => setMinutes(event.target.value)}
              />
            </div>
          </fieldset>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="assign-priority">{t('assignments.priorityLabel')}</Label>
            <select
              id="assign-priority"
              value={priority}
              onChange={(event) => setPriority(event.target.value as AssignmentPriority)}
              className="h-11 rounded-md border border-border bg-background px-3 text-sm"
            >
              {ASSIGNMENT_PRIORITIES.map((one) => (
                <option key={one} value={one}>
                  {t(`assignments.priorityValue.${one}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="assign-ot">{t('assignments.otLabel')}</Label>
            <Input
              id="assign-ot"
              type="number"
              inputMode="numeric"
              min="0"
              className="w-28"
              value={overtime}
              onChange={(event) => setOvertime(event.target.value)}
            />
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm" htmlFor="assign-splittable">
          <input
            id="assign-splittable"
            type="checkbox"
            checked={splittable}
            onChange={(event) => setSplittable(event.target.checked)}
            className="size-4 shrink-0 accent-primary"
          />
          {t('assignments.splittableLabel')}
        </label>

        {requireReason ? (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="assign-reason">{t('assignments.reasonLabel')}</Label>
            <Input
              id="assign-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder={t('assignments.reasonPlaceholder')}
            />
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={userId === '' || !reasonReady || pending}
            onClick={() =>
              onConfirm({ assignedToUserId: userId, stage, reason: reason.trim(), ...plan() })
            }
          >
            {t('assignments.assignConfirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
